// ── Contractor Pay Profile ──
// Defines how each contractor gets paid. Required before assignment to any job.
// Lightweight, enforceable, tied directly to job execution (QA-pass payout).

export type SpecArchetype =
  | "TTS-STD"      // Tub-to-Shower Standard
  | "SHWR-RPL"     // Shower Replacement / Remodel
  | "TUB-RPL"      // Tub Replacement
  | "WALK-IN-TUB"  // Walk-in Tub Install
  | "FULL-BATH"    // Full Bathroom Remodel
  | "WET-SPACE"    // Wet Space Only
  | "WINDOW"       // Window Install
  | "FLOORING";    // Flooring Install

export const SPEC_ARCHETYPES: { id: SpecArchetype; label: string }[] = [
  { id: "TTS-STD", label: "Tub-to-Shower (Standard)" },
  { id: "SHWR-RPL", label: "Shower Replacement" },
  { id: "TUB-RPL", label: "Tub Replacement" },
  { id: "WALK-IN-TUB", label: "Walk-in Tub Install" },
  { id: "FULL-BATH", label: "Full Bathroom Remodel" },
  { id: "WET-SPACE", label: "Wet Space Only" },
  { id: "WINDOW", label: "Window Install" },
  { id: "FLOORING", label: "Flooring Install" },
];

export type PaymentStructure = "full_completion";
export type PaymentTrigger = "qa_pass";
export type ContractorTier = "standard" | "preferred";

export interface ContractorPayProfile {
  id: string;                       // PP-CON-001
  contractorId: string;
  allowedSpecTypes: SpecArchetype[];
  totalPayoutAmount: number;        // fixed payout per job (USD)
  paymentStructure: PaymentStructure;
  paymentTrigger: PaymentTrigger;
  paymentSlaHours: number;          // QA SLA — auto-pay flag after this
  fieldAddCapAmount: number;        // max field-add allowed without admin approval
  tierLevel?: ContractorTier;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  notes?: string;
}

export interface PayProfileEligibility {
  allowed: boolean;
  reason: string;
}

/** Lookup helper. */
export function getPayProfile(
  profiles: ContractorPayProfile[],
  contractorId: string,
): ContractorPayProfile | undefined {
  return profiles.find((p) => p.contractorId === contractorId && p.isActive);
}

/**
 * Hard gate: a contractor cannot be assigned to a job without an active
 * Pay Profile that covers the job's spec archetype.
 */
export function canAssignContractor(
  profiles: ContractorPayProfile[],
  contractorId: string,
  specArchetype?: SpecArchetype,
): PayProfileEligibility {
  const profile = getPayProfile(profiles, contractorId);
  if (!profile) {
    return {
      allowed: false,
      reason: "No active Pay Profile. Configure during onboarding before assignment.",
    };
  }
  if (specArchetype && !profile.allowedSpecTypes.includes(specArchetype)) {
    return {
      allowed: false,
      reason: `Pay Profile does not cover spec ${specArchetype}.`,
    };
  }
  return { allowed: true, reason: "Eligible." };
}

/** Default profile shape for new entries. */
export function defaultPayProfile(contractorId: string): Omit<ContractorPayProfile, "id"> {
  const now = new Date().toISOString();
  return {
    contractorId,
    allowedSpecTypes: [],
    totalPayoutAmount: 0,
    paymentStructure: "full_completion",
    paymentTrigger: "qa_pass",
    paymentSlaHours: 24,
    fieldAddCapAmount: 150,
    tierLevel: "standard",
    isActive: true,
    createdAt: now,
    updatedAt: now,
  };
}
